export type AccessorialCode =
  | "detention"
  | "layover"
  | "lumper"
  | "tonu"
  | "redelivery"
  | "residential"
  | "liftgate"
  | "driver_assist"
  | "stop_off";

export type ChargeSide = "customer" | "carrier";

export type LoadChargeType =
  | "detention"
  | "layover"
  | "lumper"
  | "tonu"
  | "redelivery"
  | "residential"
  | "liftgate"
  | "driver_assist"
  | "stop_off"
  | "other";

export type AccessorialRateModel =
  | { kind: "flat"; amountCents: number }
  | { kind: "per_hour"; rateCents: number; freeMinutes: number; minimumMinutes: number }
  | { kind: "per_occurrence"; amountCents: number }
  | { kind: "per_stop"; amountCents: number }
  | { kind: "pass_through" };

export interface AccessorialDefinition {
  code: AccessorialCode;
  chargeType: LoadChargeType;
  label: string;
  defaultSide: ChargeSide;
  rateModel: AccessorialRateModel;
  approvalRequired: true;
  evidenceRequired: string[];
  notes: string;
}

export interface CustomerAccessorialOverride {
  customerId: string;
  code: AccessorialCode;
  disabled?: boolean;
  side?: ChargeSide;
  rateModel?: AccessorialRateModel;
  evidenceRequired?: string[];
  notes?: string;
}

export interface BuildAccessorialDraftInput {
  tenantId: string;
  loadId: string;
  code: AccessorialCode;
  side?: ChargeSide;
  description?: string;
  quantity?: number;
  amountCents?: number;
  startedAt?: string;
  endedAt?: string;
  freeMinutes?: number;
  rateCents?: number;
  evidenceDocIds?: string[];
}

export interface LoadChargeDraft {
  tenantId: string;
  loadId: string;
  side: ChargeSide;
  chargeType: LoadChargeType;
  description: string;
  amountCents: number;
  status: "drafted";
  evidenceDocIds: string[];
  approvalRequired: true;
  missingEvidence: string[];
  calculation: {
    code: AccessorialCode;
    rateModel: AccessorialRateModel["kind"];
    quantity: number;
    unitRateCents?: number;
    freeMinutes?: number;
    billableMinutes?: number;
  };
}

export const ACCESSORIAL_LIBRARY: Record<AccessorialCode, AccessorialDefinition> = {
  detention: {
    code: "detention",
    chargeType: "detention",
    label: "Detention",
    defaultSide: "customer",
    rateModel: { kind: "per_hour", rateCents: 75_00, freeMinutes: 120, minimumMinutes: 30 },
    approvalRequired: true,
    evidenceRequired: ["facility-signed detention sheet"],
    notes: "Draft only until signed facility proof is attached.",
  },
  layover: {
    code: "layover",
    chargeType: "layover",
    label: "Layover",
    defaultSide: "customer",
    rateModel: { kind: "flat", amountCents: 250_00 },
    approvalRequired: true,
    evidenceRequired: ["carrier layover notice"],
    notes: "Use when a carrier is held to the next operating day.",
  },
  lumper: {
    code: "lumper",
    chargeType: "lumper",
    label: "Lumper",
    defaultSide: "customer",
    rateModel: { kind: "pass_through" },
    approvalRequired: true,
    evidenceRequired: ["lumper receipt"],
    notes: "Pass-through amount must match the receipt unless a customer SOP says otherwise.",
  },
  tonu: {
    code: "tonu",
    chargeType: "tonu",
    label: "Truck ordered not used",
    defaultSide: "customer",
    rateModel: { kind: "flat", amountCents: 250_00 },
    approvalRequired: true,
    evidenceRequired: ["carrier arrival or dispatch proof"],
    notes: "Draft only after cancellation timing is confirmed.",
  },
  redelivery: {
    code: "redelivery",
    chargeType: "redelivery",
    label: "Redelivery",
    defaultSide: "customer",
    rateModel: { kind: "per_occurrence", amountCents: 350_00 },
    approvalRequired: true,
    evidenceRequired: ["failed delivery reason"],
    notes: "Use for a second delivery attempt or changed delivery appointment.",
  },
  residential: {
    code: "residential",
    chargeType: "residential",
    label: "Residential",
    defaultSide: "customer",
    rateModel: { kind: "per_occurrence", amountCents: 125_00 },
    approvalRequired: true,
    evidenceRequired: ["address classification"],
    notes: "Applies to pickup or delivery at a residential location.",
  },
  liftgate: {
    code: "liftgate",
    chargeType: "liftgate",
    label: "Liftgate",
    defaultSide: "customer",
    rateModel: { kind: "per_occurrence", amountCents: 150_00 },
    approvalRequired: true,
    evidenceRequired: ["facility or driver confirmation"],
    notes: "Applies when a liftgate is required at pickup or delivery.",
  },
  driver_assist: {
    code: "driver_assist",
    chargeType: "driver_assist",
    label: "Driver assist",
    defaultSide: "customer",
    rateModel: { kind: "per_hour", rateCents: 60_00, freeMinutes: 0, minimumMinutes: 60 },
    approvalRequired: true,
    evidenceRequired: ["driver assist confirmation"],
    notes: "Use for load or unload labor by the driver.",
  },
  stop_off: {
    code: "stop_off",
    chargeType: "stop_off",
    label: "Stop-off",
    defaultSide: "customer",
    rateModel: { kind: "per_stop", amountCents: 75_00 },
    approvalRequired: true,
    evidenceRequired: ["stop count"],
    notes: "Applies to extra pickup or delivery stops beyond the first pickup and delivery.",
  },
};

export const CANONICAL_ACCESSORIAL_CODES = Object.keys(ACCESSORIAL_LIBRARY) as AccessorialCode[];

export function getAccessorialDefinition(code: AccessorialCode): AccessorialDefinition {
  return ACCESSORIAL_LIBRARY[code];
}

export function applyCustomerAccessorialOverride(
  definition: AccessorialDefinition,
  override?: CustomerAccessorialOverride,
): AccessorialDefinition {
  if (!override) return definition;
  if (override.disabled) {
    return {
      ...definition,
      notes: override.notes ?? `${definition.label} disabled for this customer.`,
      evidenceRequired: ["manager override"],
    };
  }
  return {
    ...definition,
    defaultSide: override.side ?? definition.defaultSide,
    rateModel: override.rateModel ?? definition.rateModel,
    evidenceRequired: override.evidenceRequired ?? definition.evidenceRequired,
    notes: override.notes ?? definition.notes,
  };
}

export function buildAccessorialChargeDraftWithOverride(
  input: BuildAccessorialDraftInput,
  override?: CustomerAccessorialOverride,
): LoadChargeDraft {
  const definition = applyCustomerAccessorialOverride(getAccessorialDefinition(input.code), override);
  if (override?.disabled) {
    throw new Error(`${definition.label} is disabled for customer ${override.customerId}`);
  }
  return buildAccessorialDraftFromDefinition(input, definition);
}

export function buildAccessorialChargeDraft(input: BuildAccessorialDraftInput): LoadChargeDraft {
  const definition = getAccessorialDefinition(input.code);
  return buildAccessorialDraftFromDefinition(input, definition);
}

function buildAccessorialDraftFromDefinition(
  input: BuildAccessorialDraftInput,
  definition: AccessorialDefinition,
): LoadChargeDraft {
  const calculation = calculateAccessorialAmount(input, definition.rateModel);
  const evidenceDocIds = input.evidenceDocIds ?? [];

  return {
    tenantId: input.tenantId,
    loadId: input.loadId,
    side: input.side ?? definition.defaultSide,
    chargeType: definition.chargeType,
    description: input.description ?? definition.label,
    amountCents: calculation.amountCents,
    status: "drafted",
    evidenceDocIds,
    approvalRequired: true,
    missingEvidence: definition.evidenceRequired.filter((item) => !evidenceDocIds.includes(item)),
    calculation: {
      code: input.code,
      rateModel: definition.rateModel.kind,
      quantity: calculation.quantity,
      unitRateCents: calculation.unitRateCents,
      freeMinutes: calculation.freeMinutes,
      billableMinutes: calculation.billableMinutes,
    },
  };
}

function calculateAccessorialAmount(
  input: BuildAccessorialDraftInput,
  model: AccessorialRateModel,
): {
  amountCents: number;
  quantity: number;
  unitRateCents?: number;
  freeMinutes?: number;
  billableMinutes?: number;
} {
  switch (model.kind) {
    case "flat":
      return { amountCents: input.amountCents ?? model.amountCents, quantity: 1, unitRateCents: model.amountCents };
    case "per_occurrence": {
      const quantity = input.quantity ?? 1;
      const unitRateCents = input.rateCents ?? model.amountCents;
      return { amountCents: Math.round(quantity * unitRateCents), quantity, unitRateCents };
    }
    case "per_stop": {
      const quantity = input.quantity ?? 1;
      const unitRateCents = input.rateCents ?? model.amountCents;
      return { amountCents: Math.round(quantity * unitRateCents), quantity, unitRateCents };
    }
    case "pass_through": {
      if (input.amountCents === undefined) {
        throw new Error("Pass-through accessorials require amountCents");
      }
      return { amountCents: input.amountCents, quantity: 1 };
    }
    case "per_hour": {
      const billableMinutes = calculateBillableMinutes({
        startedAt: input.startedAt,
        endedAt: input.endedAt,
        freeMinutes: input.freeMinutes ?? model.freeMinutes,
        minimumMinutes: model.minimumMinutes,
      });
      const unitRateCents = input.rateCents ?? model.rateCents;
      return {
        amountCents: Math.round((billableMinutes / 60) * unitRateCents),
        quantity: billableMinutes / 60,
        unitRateCents,
        freeMinutes: input.freeMinutes ?? model.freeMinutes,
        billableMinutes,
      };
    }
  }
}

function calculateBillableMinutes(input: {
  startedAt?: string;
  endedAt?: string;
  freeMinutes: number;
  minimumMinutes: number;
}) {
  if (!input.startedAt || !input.endedAt) {
    return input.minimumMinutes;
  }

  const elapsedMinutes = Math.max(
    0,
    Math.round((Date.parse(input.endedAt) - Date.parse(input.startedAt)) / 60_000),
  );
  const billableMinutes = Math.max(0, elapsedMinutes - input.freeMinutes);
  if (billableMinutes === 0) return 0;
  return Math.max(input.minimumMinutes, roundUpToIncrement(billableMinutes, 15));
}

function roundUpToIncrement(value: number, increment: number) {
  return Math.ceil(value / increment) * increment;
}
